import { useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { subscriptions } from '@repletoryreach/sdk-api';
import { Card, Group, Stack, Text, Title, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDate } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';

export const CARD_KEY = ['billing', 'card'];

const PROVIDERS: Record<string, string> = { payfast: 'PayFast', paystack: 'Paystack' };

/** How the card reads, in the console's language: "Visa ending 4242", or the provider it is saved with. */
function cardName(t: ReturnType<typeof useT>, card: subscriptions.SavedCard): string {
  if (card.last4) {
    const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : t('billing.card.genericBrand');
    return t('billing.card.ending', { brand, last4: card.last4 });
  }
  return t('billing.card.savedWith', { provider: PROVIDERS[card.provider] ?? card.provider });
}

/** Sends the customer to PayFast to add or replace the card: a small payment that saves it and goes into their credit. */
export function useAddCard() {
  const t = useT();
  return useMutation({
    mutationFn: () => subscriptions.addCard(`${window.location.origin}/billing?topup=returned`),
    onSuccess: (r) => window.location.assign(r.data.checkout_url),
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.card.title'))
  });
}

/**
 * The card a plan is charged to (owner's decision of 11 October 2026: plans are paid by card every month, not from
 * the credit balance). Shows the card with Replace, or, on a paid plan with none, asks for one. An email's "Update
 * your card" link opens the billing page with <c>?card=update</c>, which brings this card into view.
 */
export function PaymentCard({ paidPlan, focus }: { paidPlan: boolean; focus: boolean }) {
  const t = useT();
  const cardQ = useQuery({ queryKey: CARD_KEY, queryFn: () => subscriptions.getCard(), retry: false });
  const add = useAddCard();
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (focus) ref.current?.scrollIntoView({ block: 'center' }); }, [focus]);
  const card = cardQ.data?.data.card ?? null;

  return (
    <div ref={ref}>
      <Card withBorder data-guide="billing-card" data-testid="payment-card" className={focus ? 'rr-billing__focus' : undefined}>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
            <Stack gap={2} style={{ flex: '1 1 280px' }}>
              <Title order={4}>{t('billing.card.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.card.description')}</Text>
            </Stack>
            <Button size="sm" variant={card ? 'default' : 'filled'} loading={add.isPending} onClick={() => add.mutate()} data-testid="add-card">
              {card ? t('billing.card.replace') : t('billing.card.add')}
            </Button>
          </Group>
          {card ? (
            <Text role="bodySmall" data-testid="card-label">
              {t('billing.card.current', { card: cardName(t, card), date: formatDate(card.added_at) })}
            </Text>
          ) : paidPlan ? (
            <Alert color="warning">{t('billing.card.missing')}</Alert>
          ) : (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.card.none')}</Text>
          )}
          <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.card.checkHint')}</Text>
        </Stack>
      </Card>
    </div>
  );
}
